import React from "react";

import { bemClassNames } from "@multiweb/reactUtils";
import { FetchingState, KeyGeneratorType, RowRenderer } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { InfiniteVirtualizedList, InfiniteVirtualizedListProps } from "../InfiniteVirtualizedList";
import { ResponsiveList } from "../ResponsiveList";
import { DataGridVariant } from "../types";

export type DataGridProps<T> = {
    /** Data pro vykreslení položek. */
    items: ReadonlyArray<T>;
    /** Určuje, zdali se budou data dočítat postupně, nebo se načtou jen do viditelné části. */
    loadOnScroll: boolean;
    /** Určuje, zda se načítají další data. */
    fetchingState: FetchingState;
    /** Počet dat, které se právě načítají. */
    fetchingCount: number;
    /** Určuje, zda je možné načítat další data. */
    canLoadData: boolean;
    /** Zaskroluje na daný index. Použito ve VirtualizedListu. */
    scrollToIndex?: number;
    /** Počet dat k dočtění, určuje počet skeletonů pro VirtualizedList. */
    dataCountToLoad?: number;
    /** Upravuje výchozí chování vzhledu. */
    variant?: DataGridVariant;
    /** Odhad výšky záznamu pro InfiniteVirtualizedList. */
    estimatedRowSize?: number;
    /** Akce pro načtení dalších dat. */
    loadData: (count: number) => void;
    /** Funkce pro generování klíčů položek. */
    keyGenerator: KeyGeneratorType<T>;
    /** Funkce, která vrací pole skeletonů. */
    createSkeletonRowsKeys: (count: number) => ReadonlyArray<T>;
    /** Pokud v DataGridu nejsou žádná data, vykreslí element. */
    noRowsRenderer: () => JSX.Element;
    /** Vykreslí jednotlivé položky. */
    itemRenderer: RowRenderer<T>;
} & Pick<InfiniteVirtualizedListProps<T>, "onScroll">;

const classNames = bemClassNames("data-grid-component");

export const DataGrid = <T,>({
    items,
    loadOnScroll,
    fetchingState,
    fetchingCount,
    canLoadData,
    dataCountToLoad,
    estimatedRowSize,
    scrollToIndex,
    variant = DataGridVariant.default,
    loadData,
    keyGenerator,
    createSkeletonRowsKeys,
    noRowsRenderer,
    itemRenderer,
    onScroll,
}: DataGridProps<T>) => (
        <div className={classNames({ variant })} role={Role.grid} aria-multiselectable={true} >
            {loadOnScroll ? (
                <InfiniteVirtualizedList
                    cellMeasureCacheParams={{ fixedWidth: true }}
                    loadNextItems={loadData}
                    canLoadNextItems={canLoadData}
                    {...{ items, fetchingState, keyGenerator, createSkeletonRowsKeys, noRowsRenderer, itemRenderer, dataCountToLoad, variant, estimatedRowSize, scrollToIndex, onScroll }}
                />
            ) : (
                <ResponsiveList
                    loadNextItems={loadData}
                    canLoadNextItems={canLoadData}
                    {...{ items, fetchingState, fetchingCount, keyGenerator, createSkeletonRowsKeys, noRowsRenderer, itemRenderer }}
                />
            )}
        </div>
    );
